import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, Check as CheckIcon } from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection } from '../common';

const QUOTE_COLORS = [
  '#8B5CF6', '#EC4899', '#10B981', '#F59E0B',
  '#3B82F6', '#EF4444', '#06B6D4', '#84CC16',
];

// Quote Form Dialog
interface QuoteData {
  text: string;
  author?: string;
  date?: string;
  personId?: string;
  context?: string;
  color?: string;
  authorAge?: number;
}

interface QuoteFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: QuoteData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  quote?: { id: string; family_id?: string; data?: { data?: QuoteData } } | null;
}

export const QuoteFormDialog: React.FC<QuoteFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  quote,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!quote;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(quote?.family_id ? [quote.family_id] : undefined);

  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [date, setDate] = useState('');
  const [personId, setPersonId] = useState('');
  const [context, setContext] = useState('');
  const [color, setColor] = useState(QUOTE_COLORS[0]);
  const [authorAge, setAuthorAge] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (quote) {
        const data = (quote.data?.data || {}) as any;
        setText(data.text || '');
        setAuthor(data.author || '');
        setDate(data.date || '');
        setPersonId(data.personId || data.author_id || '');
        setContext(data.context || '');
        setColor(data.color || QUOTE_COLORS[0]);
        setAuthorAge(data.authorAge?.toString() || data.author_age?.toString() || '');
      } else {
        setText('');
        setAuthor('');
        setDate(new Date().toISOString().split('T')[0]);
        setPersonId('');
        setContext('');
        setColor(QUOTE_COLORS[0]);
        setAuthorAge('');
      }
      setError(null);
    }
  }, [open, quote]);

  const handleSave = async () => {
    if (!text.trim()) {
      setError('Quote text is required');
      return;
    }

    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          text: text.trim(),
          author: author.trim() || undefined,
          date: date || undefined,
          personId: personId || undefined,
          context: context.trim() || undefined,
          color,
          authorAge: authorAge ? parseInt(authorAge) : undefined,
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete quote');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Quote' : 'New Quote'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Quote"
              value={text}
              onChange={(e) => setText(e.target.value)}
              fullWidth
              required
              autoFocus
              multiline
              rows={3}
              placeholder="What did they say?"
            />
          </Grid>

          {/* Color Picker */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Color</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {QUOTE_COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setColor(c)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: color === c ? '3px solid' : '2px solid transparent',
                    borderColor: color === c ? 'text.primary' : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { transform: 'scale(1.15)' },
                  }}
                >
                  {color === c && <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />}
                </Box>
              ))}
            </Box>
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 5 }}>
            <FormControl fullWidth>
              <InputLabel>Said By</InputLabel>
              <Select value={personId} label="Said By" onChange={(e) => setPersonId(e.target.value)}>
                <MenuItem value=""><em>Unknown</em></MenuItem>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>
                        {getMemberName(member.id).charAt(0)}
                      </Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Age"
              value={authorAge}
              onChange={(e) => setAuthorAge(e.target.value)}
              fullWidth
              type="number"
              placeholder="e.g., 5"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              fullWidth
              placeholder="What was happening when they said this?"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Album Form Dialog
interface AlbumData {
  name: string;
  description?: string;
  date?: string;
}

interface AlbumFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AlbumData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  album?: { id: string; family_id?: string; data?: { data?: AlbumData } } | null;
}

export const AlbumFormDialog: React.FC<AlbumFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  album,
}) => {
  const isEditing = !!album;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(album?.family_id ? [album.family_id] : undefined);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (album) {
        const data = (album.data?.data || {}) as any;
        setName(data.name || '');
        setDescription(data.description || '');
        setDate(data.date || '');
      } else {
        setName('');
        setDescription('');
        setDate('');
      }
      setError(null);
    }
  }, [open, album]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Album name is required');
      return;
    }

    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          date: date || undefined,
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save album');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete album');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Album' : 'New Album'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Album Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              autoFocus
              placeholder="e.g., Summer Vacation 2024, Birthday Party"
            />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default {
  QuoteFormDialog,
  AlbumFormDialog,
};
